import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/datetime";
import { formatPhone, formatPhoneInput } from "@/lib/phone";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Pencil, UserPlus, PackagePlus, Phone, MapPin, Search, X, RefreshCw, GitMerge } from "lucide-react";
import PatientOrdersDialog from "@/components/PatientOrdersDialog";
import TopPatientsChart from "@/components/TopPatientsChart";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ITEM_OPTIONS = ["Regular Medication", "Controlled Medication", "Refrigerated", "Other"];
const DELIVER_TYPES = [
  { v: "same_day", label: "Same Day" },
  { v: "next_day", label: "Next Day" },
  { v: "otc", label: "OTC" },
  { v: "pickup", label: "Pick Up" },
];

function PatientFormDialog({ open, onOpenChange, initial, pharmacies, lockedPharmacyId, onSubmit, title, submitLabel }) {
  const [form, setForm] = useState({ name: "", phone: "", address: "", pharmacy_id: "", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm({
      name: initial?.name || "",
      phone: initial?.phone || "",
      address: initial?.address || "",
      pharmacy_id: initial?.pharmacy_id || lockedPharmacyId || pharmacies?.[0]?.id || "",
      notes: initial?.notes || "",
    });
  }, [initial, open, lockedPharmacyId, pharmacies]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.pharmacy_id) { toast.error("Select a pharmacy"); return; }
    setBusy(true);
    try { await onSubmit(form); onOpenChange(false); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="patient-form">
          <div>
            <Label>Pharmacy</Label>
            <Select value={form.pharmacy_id} onValueChange={(v) => setForm({ ...form, pharmacy_id: v })} disabled={!!lockedPharmacyId}>
              <SelectTrigger data-testid="patient-pharmacy"><SelectValue placeholder="Select pharmacy" /></SelectTrigger>
              <SelectContent>
                {(pharmacies || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Patient name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="patient-name" /></div>
          <div><Label>Phone</Label><Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: formatPhoneInput(e.target.value) })} placeholder="305-555-1234" data-testid="patient-phone" /></div>
          <div><Label>Address</Label><Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="patient-address" /></div>
          <div><Label>Notes (optional)</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <Button type="submit" disabled={busy} className="rounded-full w-full" data-testid="patient-submit">{busy ? "Saving…" : submitLabel}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateOrderFromPatient({ patient, pharmacyName, orderNumberingMode, open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const canSetCopay = user?.role === "super_admin" || user?.role === "pharmacy_staff";
  const [items, setItems] = useState([{ name: "Regular Medication", qty: 1, notes: "" }]);
  const [deliverType, setDeliverType] = useState("same_day");
  const [priority, setPriority] = useState("normal");
  const [notes, setNotes] = useState("");
  const [copayEnabled, setCopayEnabled] = useState(false);
  const [copay, setCopay] = useState("");
  // Manual Order # — only for pharmacies whose `order_numbering === "manual"`.
  // Optional even for manual pharmacies (server still auto-assigns if left blank).
  const [orderNumber, setOrderNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const isManualNumbering = orderNumberingMode === "manual";

  useEffect(() => {
    if (open) {
      setItems([{ name: "Regular Medication", qty: 1, notes: "" }]);
      setDeliverType("same_day"); setPriority("normal");
      // Pre-fill Delivery notes with the patient's saved notes so dispatchers
      // don't have to re-type recurring instructions (e.g. "Leave at gate B").
      setNotes(patient?.notes || "");
      setCopayEnabled(false); setCopay("");
      setOrderNumber("");
    }
  }, [open, patient?.notes]);

  const setItem = (i, k, v) => setItems((a) => a.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((a) => [...a, { name: "Regular Medication", qty: 1, notes: "" }]);
  const removeItem = (i) => setItems((a) => (a.length > 1 ? a.filter((_, idx) => idx !== i) : a));

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = items.filter((it) => it.name.trim()).map((it) => ({ ...it, qty: Number(it.qty) || 1 }));
    if (!cleaned.length) { toast.error("Add at least one item"); return; }
    setBusy(true);
    try {
      const payload = {
        customer_name: patient.name, customer_phone: patient.phone, address: patient.address,
        pharmacy_id: patient.pharmacy_id, items: cleaned, priority, deliver_type: deliverType, notes: notes || null,
      };
      if (canSetCopay && copayEnabled && copay !== "") {
        const n = Number(copay);
        if (Number.isFinite(n) && n >= 0) payload.copay = n;
      }
      // Only send the manual Order # when the pharmacy is on manual numbering
      // AND the operator actually typed something. Empty = server auto-assigns.
      if (isManualNumbering) {
        const trimmed = (orderNumber || "").trim();
        if (trimmed) payload.order_number = trimmed;
      }
      await api.post("/orders", payload);
      toast.success(`Order created for ${patient.name}`);
      onCreated?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="create-order-from-patient">
        <DialogHeader><DialogTitle>New order for {patient?.name}</DialogTitle></DialogHeader>
        <div className="space-y-1 mb-2 p-3 rounded-lg bg-secondary/40 text-sm">
          <div className="font-medium">{patient?.name}</div>
          <div className="text-muted-foreground flex items-center gap-1.5"><Phone className="h-3 w-3" />{formatPhone(patient?.phone)}</div>
          <div className="text-muted-foreground flex items-center gap-1.5"><MapPin className="h-3 w-3" />{patient?.address}</div>
          <div className="text-xs text-muted-foreground mt-1">From {pharmacyName}</div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Deliver type</Label>
              <Select value={deliverType} onValueChange={setDeliverType}>
                <SelectTrigger data-testid="new-order-deliver-type"><SelectValue /></SelectTrigger>
                <SelectContent>{DELIVER_TYPES.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="cold_chain">Cold Chain</SelectItem>
                  <SelectItem value="clinic">Clinic</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Items</Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <div className="col-span-7">
                    <Select value={it.name} onValueChange={(v) => setItem(i, "name", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{ITEM_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Input className="col-span-2" type="number" min={1} value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} />
                  <Input className="col-span-2" placeholder="notes" value={it.notes} onChange={(e) => setItem(i, "notes", e.target.value)} />
                  <Button type="button" variant="ghost" size="icon" className="col-span-1" onClick={() => removeItem(i)}><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label>Delivery notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Apartment buzzer, allergies…" />
          </div>
          {isManualNumbering && (
            <div data-testid="manual-order-number-section">
              <Label>
                Order # <span className="text-muted-foreground text-xs font-normal">(this pharmacy uses manual numbering · leave blank to auto-assign)</span>
              </Label>
              <Input
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="e.g. RX00123"
                maxLength={40}
                data-testid="manual-order-number-input"
              />
            </div>
          )}
          {canSetCopay && (
            <div data-testid="quick-copay-section">
              <Label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={copayEnabled}
                  onCheckedChange={(v) => { setCopayEnabled(!!v); if (!v) setCopay(""); }}
                  data-testid="quick-copay-toggle"
                />
                <span className="text-sm">Charge a copay</span>
              </Label>
              {copayEnabled && (
                <div className="relative mt-1.5">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={copay}
                    onChange={(e) => setCopay(e.target.value)}
                    placeholder="0.00"
                    className="pl-7"
                    data-testid="quick-copay"
                    autoFocus
                  />
                </div>
              )}
            </div>
          )}
          <Button type="submit" disabled={busy} className="rounded-full w-full" data-testid="new-order-submit">{busy ? "Creating…" : "Create order"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPatients() {
  const [pharmacies, setPharmacies] = useState([]);
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [filterPh, setFilterPh] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [editPatient, setEditPatient] = useState(null);
  const [orderTarget, setOrderTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);

  const pharmacyMap = useMemo(() => Object.fromEntries(pharmacies.map((p) => [p.id, p.name])), [pharmacies]);
  // Lookup the full pharmacy record so the New Order dialog knows whether
  // the patient's pharmacy uses manual order numbering.
  const pharmacyById = useMemo(() => Object.fromEntries(pharmacies.map((p) => [p.id, p])), [pharmacies]);

  const load = async () => {
    try {
      const params = filterPh !== "all" ? { pharmacy_id: filterPh } : {};
      const r = await api.get("/patients", { params });
      setPatients(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { api.get("/pharmacies").then((r) => setPharmacies(r.data)); }, []);
  useEffect(() => { load(); }, [filterPh]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter((p) =>
      [p.name, p.phone, p.address, p.notes, pharmacyMap[p.pharmacy_id]].some((v) => v && v.toLowerCase().includes(q))
    );
  }, [patients, search, pharmacyMap]);

  const createPatient = async (form) => { await api.post("/patients", form); toast.success("Patient added"); load(); };
  const updatePatient = async (form) => { await api.put(`/patients/${editPatient.id}`, form); toast.success("Patient updated"); setEditPatient(null); load(); };
  const deletePatient = async (p) => {
    if (!confirm(`Delete patient ${p.name}?`)) return;
    try { await api.delete(`/patients/${p.id}`); toast.success("Patient removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // ---- Merge duplicates ----
  // Row-level multi-select + a confirm dialog that lets the user pick which
  // record to keep. Only same-pharmacy rows can be merged (enforced server-
  // side); we surface a clear error toast if the user tries a cross-pharmacy
  // set instead of silently disabling the button.
  const [selected, setSelected] = useState(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeWinner, setMergeWinner] = useState(null);
  const toggleSel = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const clearSel = () => setSelected(new Set());
  const selectedPatients = useMemo(
    () => filtered.filter((p) => selected.has(p.id)),
    [filtered, selected]
  );
  const openMerge = () => {
    if (selectedPatients.length < 2) return;
    const firstPh = selectedPatients[0].pharmacy_id;
    if (!selectedPatients.every((p) => p.pharmacy_id === firstPh)) {
      toast.error("Select duplicates from the same pharmacy only.");
      return;
    }
    setMergeWinner(selectedPatients[0].id);
    setMergeOpen(true);
  };
  const runMerge = async () => {
    const loser_ids = selectedPatients.filter((p) => p.id !== mergeWinner).map((p) => p.id);
    if (!mergeWinner || loser_ids.length === 0) return;
    try {
      const r = await api.post("/admin/patients/merge", { winner_id: mergeWinner, loser_ids });
      toast.success(`Merged ${r.data.merged_count} duplicate(s). Kept record now has ${r.data.total_orders} orders.`);
      setMergeOpen(false); setMergeWinner(null); clearSel(); load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] mx-auto" data-testid="admin-patients-page">
      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl lg:text-4xl font-display font-bold tracking-tight">Patients</h1>
          <p className="text-muted-foreground mt-1.5">Every patient registered across all pharmacies.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="rounded-full"
            data-testid="rebuild-patient-registry-btn"
            title="Re-scan every order and add any patient that isn't on the list yet. Safe to run multiple times."
            onClick={async () => {
              if (!window.confirm(
                "Rebuild the Patient List from every order in the database?\n\n" +
                "This adds any patient missed by past Excel bulk imports " +
                "(e.g. rows without a phone number). Safe to run multiple times."
              )) return;
              try {
                const r = await api.post("/admin/patients/backfill");
                alert(`Rebuild complete.\n\nOrders scanned: ${r.data.orders_scanned}\nPatients now on list: ${r.data.patients_total}`);
                load();
              } catch (e) {
                alert(`Rebuild failed: ${e?.response?.data?.detail || e.message}`);
              }
            }}
          >
            <RefreshCw className="h-4 w-4 mr-1.5" /> Rebuild from orders
          </Button>
          <Button className="rounded-full" onClick={() => setAddOpen(true)} data-testid="add-patient-btn">
            <UserPlus className="h-4 w-4 mr-1.5" /> Add patient
          </Button>
        </div>
      </header>

      <PatientFormDialog open={addOpen} onOpenChange={setAddOpen} initial={null} pharmacies={pharmacies} onSubmit={createPatient} title="New patient" submitLabel="Add patient" />
      <PatientFormDialog open={!!editPatient} onOpenChange={(o) => !o && setEditPatient(null)}
        initial={editPatient} pharmacies={pharmacies} lockedPharmacyId={editPatient?.pharmacy_id}
        onSubmit={updatePatient} title="Edit patient" submitLabel="Save changes" />

      <TopPatientsChart />
      <CreateOrderFromPatient
        patient={orderTarget}
        pharmacyName={orderTarget ? pharmacyMap[orderTarget.pharmacy_id] : ""}
        orderNumberingMode={orderTarget ? (pharmacyById[orderTarget.pharmacy_id]?.order_numbering || "auto") : "auto"}
        open={!!orderTarget}
        onOpenChange={(o) => !o && setOrderTarget(null)}
        onCreated={load}
      />
      <PatientOrdersDialog patient={historyTarget} open={!!historyTarget} onOpenChange={(o) => !o && setHistoryTarget(null)} />

      <Card className="rounded-xl border-border bg-card/80 overflow-hidden">
        <div className="px-5 py-3 bg-secondary/50 border-b border-border flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs uppercase tracking-wider font-medium text-muted-foreground">
            Patients ({filtered.length}{(search || filterPh !== "all") ? ` / ${patients.length}` : ""})
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-48">
              <Select value={filterPh} onValueChange={setFilterPh}>
                <SelectTrigger data-testid="admin-patients-pharmacy-filter" className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All pharmacies</SelectItem>
                  {pharmacies.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, phone, address, pharmacy…"
                className="pl-8 h-9 text-sm"
                data-testid="admin-patient-search"
              />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          {selected.size > 0 && (
            <div className="flex items-center justify-between gap-3 px-5 py-2 bg-amber-100 border-b border-amber-300 text-sm dark:bg-amber-900/20 dark:border-amber-800" data-testid="patient-merge-bar">
              <div className="flex items-center gap-3">
                <span className="font-semibold text-amber-900 dark:text-amber-200">
                  {selected.size} patient{selected.size === 1 ? "" : "s"} selected
                </span>
                <Button variant="ghost" size="sm" onClick={clearSel} className="text-amber-800 hover:text-amber-900 h-7">
                  Clear
                </Button>
              </div>
              <Button
                size="sm"
                onClick={openMerge}
                disabled={selected.size < 2}
                className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="patient-merge-open-btn"
                title={selected.size < 2 ? "Select at least 2 rows to merge" : "Merge selected duplicates"}
              >
                <GitMerge className="h-3.5 w-3.5 mr-1.5" /> Merge {selected.size >= 2 ? `${selected.size} duplicates` : "duplicates"}
              </Button>
            </div>
          )}
          <table className="w-full text-sm" data-testid="admin-patients-table">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="pl-5 pr-1 py-2 w-8"></th>
                <th className="px-5 py-2 font-medium">Patient</th>
                <th className="px-5 py-2 font-medium">Pharmacy</th>
                <th className="px-5 py-2 font-medium">Phone</th>
                <th className="px-5 py-2 font-medium">Address</th>
                <th className="px-5 py-2 font-medium">Orders</th>
                <th className="px-5 py-2 font-medium">Last order</th>
                <th className="px-5 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className={`border-b border-border hover:bg-secondary/30 ${selected.has(p.id) ? "bg-amber-50 dark:bg-amber-900/10" : ""}`} data-testid={`patient-row-${p.id}`}>
                  <td className="pl-5 pr-1 py-3">
                    <Checkbox
                      checked={selected.has(p.id)}
                      onCheckedChange={() => toggleSel(p.id)}
                      data-testid={`patient-select-${p.id}`}
                      aria-label={`Select ${p.name} for merge`}
                    />
                  </td>
                  <td className="px-5 py-3 font-medium">
                    <button
                      type="button"
                      onClick={() => setHistoryTarget(p)}
                      className="text-primary hover:underline underline-offset-2 text-left"
                      data-testid={`patient-name-link-${p.id}`}
                      title="View order history"
                    >
                      {p.name}
                    </button>
                  </td>
                  <td className="px-5 py-3 text-sm text-muted-foreground">{pharmacyMap[p.pharmacy_id] || "—"}</td>
                  <td className="px-5 py-3 text-muted-foreground font-mono-num text-xs">{formatPhone(p.phone)}</td>
                  <td className="px-5 py-3 text-muted-foreground max-w-[280px] truncate">{p.address}</td>
                  <td className="px-5 py-3 font-mono-num text-xs">{p.total_orders || 0}</td>
                  <td className="px-5 py-3 text-xs text-muted-foreground">{p.last_order_at ? fmtDate(p.last_order_at) : "—"}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <Button size="icon" onClick={() => setOrderTarget(p)} className="h-9 w-9 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid={`new-order-btn-${p.id}`} title="Create new order">
                      <PackagePlus className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setEditPatient(p)} className="text-muted-foreground hover:text-primary" data-testid={`patient-edit-${p.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => deletePatient(p)} className="text-muted-foreground hover:text-destructive" data-testid={`patient-delete-${p.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-12 text-center text-muted-foreground text-sm">
                  {patients.length === 0
                    ? `No patients registered yet.`
                    : `No patients match your filters.`}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ---- Merge duplicates confirm dialog ---- */}
      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent className="max-w-lg" data-testid="patient-merge-dialog">
          <DialogHeader>
            <DialogTitle>Merge duplicate patients</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            Pick the record to <strong>keep</strong>. Missing fields (phone, address) will be filled in from the others, and total orders will be summed. The other records will be deleted.
          </div>
          <div className="mt-3 border rounded-lg divide-y">
            {selectedPatients.map((p) => (
              <label
                key={p.id}
                className={`flex items-start gap-2 p-3 cursor-pointer hover:bg-secondary/40 ${mergeWinner === p.id ? "bg-emerald-50 dark:bg-emerald-900/20" : ""}`}
                data-testid={`patient-merge-choice-${p.id}`}
              >
                <input
                  type="radio"
                  name="merge-winner"
                  className="mt-1"
                  checked={mergeWinner === p.id}
                  onChange={() => setMergeWinner(p.id)}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium">
                    {p.name}
                    {mergeWinner === p.id && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 font-semibold">Keep</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatPhone(p.phone) || "no phone"} · {p.address || "no address"}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {p.total_orders || 0} orders · Last: {p.last_order_at ? fmtDate(p.last_order_at) : "—"}
                  </div>
                </div>
              </label>
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setMergeOpen(false)} data-testid="patient-merge-cancel">Cancel</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={runMerge}
              disabled={!mergeWinner || selectedPatients.length < 2}
              data-testid="patient-merge-confirm"
            >
              <GitMerge className="h-4 w-4 mr-1.5" /> Merge {selectedPatients.length - 1} into 1
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
